/* eslint-disable @typescript-eslint/no-shadow */
import {
  CreateTechnicLibraryInput as CreateInput,
  CreateTechnicLibraryMutationVariables as CreateMutationVariables,
  DeleteEntityByIdMutationVariables,
  DeleteTechnicLibraryMutationVariables as DeleteMutationVariables,
  DeepDeletableEntityType,
  MutationStatus,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  OnCreateTechnicLibrarySubscription,
  OnDeleteTechnicLibrarySubscription,
  OnUpdateTechnicLibrarySubscription,
  removeNestedFieldsExcept,
  TechnicLibrary,
  TechnicLibraryWithRules,
  attachRulesToTechnicLibrary,
  cleanInputCreate,
  cleanInputUpdate,
  getReadId,
  getTableClientId,
  onCreateTechnicLibrary,
  onDeleteTechnicLibrary,
  onUpdateTechnicLibrary,
  createTechnicLibrary as createMutation,
  deleteTechnicLibrary as deleteMutation,
  updateTechnicLibrary as updateMutation,
  technicBulkRename as technicBulkRenameMutation,
  getTechnicLibrary as getQuery,
  syncTechnicLibraries,
  CreateTechnicLibraryInput,
  TECHNIC_LIBRARY_TABLE_NAME,
  addMaintenanceSchemaProperties,
  deleteEntityById as deleteEntityByIdMutation,
  addCertificateValidityGracePeriod,
  todayDateTime,
} from '@rentguru/commons-utils';
import {
  cleanMixedDatastoreAndAPISubscription,
  deleteEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  mutationWithFetchBefore,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { TechnicBulkRenameScope } from 'src/components/Technics/technicLibraryMapping';

import { ContextLoaderAction, ContextLoaderStore, useContextLoader } from './ContextLoader';
import { useRules } from './RuleContext';
import { useUser } from './UserContext';

export interface TechnicRenameProposal {
  technicLibrary: TechnicLibrary;
  oldFormat?: string;
  scope?: TechnicBulkRenameScope;
}

const withTechnicLibraryProperties = <T extends string>(query: T): T =>
  addCertificateValidityGracePeriod(addMaintenanceSchemaProperties(query)) as T;
const customCreateTechnicLibrary = withTechnicLibraryProperties(createMutation);
const customUpdateTechnicLibrary = withTechnicLibraryProperties(updateMutation);
const customGetTechnicLibrary = withTechnicLibraryProperties(getQuery);
const customOnCreateTechnicLibrary = withTechnicLibraryProperties(
  cleanMixedDatastoreAndAPISubscription(onCreateTechnicLibrary)
);
const customOnUpdateTechnicLibrary = withTechnicLibraryProperties(
  cleanMixedDatastoreAndAPISubscription(onUpdateTechnicLibrary)
);
const customOnDeleteTechnicLibrary = withTechnicLibraryProperties(
  cleanMixedDatastoreAndAPISubscription(onDeleteTechnicLibrary)
);

interface TechnicBulkRenameInput {
  technicLibraryId: string;
  newFormat: string;
  includeManuallyNamed: boolean;
  oldFormat?: string;
  buildingId?: string;
  unitId?: string;
}

interface TechnicBulkRenameMutationVariables {
  input: TechnicBulkRenameInput;
}

export interface TechnicBulkRenameOptions {
  oldFormat?: string;
  buildingId?: string;
  unitId?: string;
}

export type ActionTechnicLibrary =
  | {
      type: 'SHOULD_FETCH_TECHNIC_LIBRARY' | 'IS_FETCHING_TECHNIC_LIBRARY';
    }
  | {
      type: 'ADD_TECHNIC_LIBRARY';
      payload: { technicLibrary: TechnicLibrary };
    }
  | {
      type: 'FETCHED_TECHNIC_LIBRARY';
      payload: { technicLibraries: TechnicLibrary[] };
    }
  | {
      type: 'UPDATE_TECHNIC_LIBRARY';
      payload: { technicLibrary: TechnicLibrary };
    }
  | {
      type: 'DELETE_TECHNIC_LIBRARY';
      payload: { id: string };
    };

export interface TechnicsLibraryContext {
  technicLibraries: TechnicLibraryWithRules[];
  getTechnicLibrary: (id: string) => TechnicLibraryWithRules | undefined;
  createTechnicLibrary: (
    input: Omit<CreateTechnicLibraryInput, 'id' | 'clientId' | 'readId'>
  ) => Promise<TechnicLibrary>;
  updateTechnicLibrary: (original: TechnicLibrary, updates: Partial<TechnicLibrary>) => Promise<TechnicLibrary>;
  technicBulkRename: (
    technicLibraryId: string,
    newFormat: string,
    includeManuallyNamed: boolean,
    options?: TechnicBulkRenameOptions
  ) => Promise<MutationStatus | undefined>;
  deleteTechnicLibrary: (id: string) => Promise<TechnicLibrary | undefined>;
  deepDeleteTechnicLibrary: (id: string) => Promise<MutationStatus | undefined>;
  technicLibrariesLoading: boolean | undefined;
  technicLibrariesError: string | undefined;
  setFetchTechnicLibrary: () => void;
  renameProposalQueue: TechnicRenameProposal[];
  suggestRenameForFormatChange: (technicLibrary: TechnicLibrary, oldFormat: string) => void;
  dequeueTechnicRenameProposal: () => void;
}

export const technicLibraryReducerDelegation = (
  state: ContextLoaderStore,
  action: ContextLoaderAction
): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_TECHNIC_LIBRARY':
      if (state.TechnicLibrary.loading || state.TechnicLibrary.shouldFetch) {
        return state;
      }
      return {
        ...state,
        TechnicLibrary: { ...state.TechnicLibrary, shouldFetch: true },
      };
    case 'IS_FETCHING_TECHNIC_LIBRARY':
      if (state.TechnicLibrary.loading) {
        return state;
      }
      return {
        ...state,
        TechnicLibrary: { ...state.TechnicLibrary, loading: true },
      };
    case 'FETCHED_TECHNIC_LIBRARY':
      return {
        ...state,
        TechnicLibrary: {
          ...state.TechnicLibrary,
          data: action.payload.technicLibraries,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_TECHNIC_LIBRARY':
      if (state.TechnicLibrary.data?.find((object) => object.id === action.payload.technicLibrary.id)) {
        return state;
      }
      return {
        ...state,
        TechnicLibrary: {
          ...state.TechnicLibrary,
          data: [...state.TechnicLibrary.data, action.payload.technicLibrary],
        },
      };
    case 'UPDATE_TECHNIC_LIBRARY':
      if (!state.TechnicLibrary.data) {
        return state;
      }
      const currentObject = state.TechnicLibrary.data?.find((object) => object.id === action.payload.technicLibrary.id);
      if (!currentObject) {
        return {
          ...state,
          TechnicLibrary: {
            ...state.TechnicLibrary,
            data: [...state.TechnicLibrary.data, action.payload.technicLibrary],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.technicLibrary)) {
        return state;
      }
      return {
        ...state,
        TechnicLibrary: {
          ...state.TechnicLibrary,
          data: state.TechnicLibrary.data.map((object) => {
            if (object.id === action.payload.technicLibrary.id) {
              return action.payload.technicLibrary;
            }
            return object;
          }),
        },
      };
    case 'DELETE_TECHNIC_LIBRARY':
      if (!state.TechnicLibrary.data) {
        return state;
      }
      return {
        ...state,
        TechnicLibrary: {
          ...state.TechnicLibrary,
          data: state.TechnicLibrary.data.filter((object) => object.id !== action.payload.id),
        },
      };
    default:
      return state;
  }
};

const TECHNIC_LIBRARY_RELATIONS_TO_REMOVE = ['customFields'];
const TECHNIC_LIBRARY_NESTED_FIELDS_TO_KEEP = [
  'certificateValidity',
  'descriptions',
  'maintenanceSchemas',
  'names',
  'validity',
];

const TECHNIC_LIBRARIES_QUERY = withTechnicLibraryProperties(
  removeNestedFieldsExcept(
    syncTechnicLibraries,
    DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
    TECHNIC_LIBRARY_RELATIONS_TO_REMOVE,
    TECHNIC_LIBRARY_NESTED_FIELDS_TO_KEEP
  )
);

const fetchTechnicLibraries = async (
  by: 'byClientId',
  byValue: string,
  additionalFilter?: object
): Promise<TechnicLibrary[]> => {
  return await list<TechnicLibrary>(TECHNIC_LIBRARIES_QUERY, getFilterFieldNameForIndex(by), byValue, additionalFilter);
};

export const TechnicsLibraryContext = createContext<TechnicsLibraryContext | null>(null);

export const TechnicsLibraryContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const {
    TechnicLibrary: { data: technicLibrariesLoader, loading: technicLibrariesLoading, shouldFetch },
    dispatch: contextDispatch,
  } = useContextLoader();
  const { clientId } = useUser();
  const { rules } = useRules();

  const technicLibraries = useMemo<TechnicLibraryWithRules[]>(() => {
    if (technicLibrariesLoading) {
      return [];
    }
    return (technicLibrariesLoader || []).map((technicLibrary) =>
      attachRulesToTechnicLibrary(technicLibrary, rules ?? [])
    );
  }, [technicLibrariesLoader, technicLibrariesLoading, rules]);

  const setFetchTechnicLibrary = () => {
    contextDispatch({ type: 'SHOULD_FETCH_TECHNIC_LIBRARY' });
  };

  const [renameProposalQueue, setRenameProposalQueue] = useState<TechnicRenameProposal[]>([]);

  const enqueueTechnicRenameProposals = (proposals: TechnicRenameProposal[]) => {
    if (isEmpty(proposals)) {
      return;
    }
    setRenameProposalQueue((queue) => [...queue, ...proposals]);
  };

  const suggestRenameForFormatChange = (technicLibrary: TechnicLibrary, oldFormat: string) => {
    enqueueTechnicRenameProposals([{ technicLibrary, oldFormat }]);
  };

  const dequeueTechnicRenameProposal = () => {
    setRenameProposalQueue((queue) => queue.slice(1));
  };

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_TECHNIC_LIBRARY' });
      try {
        const result = await fetchTechnicLibraries(
          'byClientId',
          getTableClientId(clientId!, TECHNIC_LIBRARY_TABLE_NAME)
        );
        contextDispatch({ type: 'FETCHED_TECHNIC_LIBRARY', payload: { technicLibraries: result } });
      } catch (error) {
        console.error('[technicsLibrary] fetching technic libraries failed', error);
        contextDispatch({ type: 'FETCH_FAILED', payload: { model: 'TechnicLibrary' } });
      }
    };
    if (shouldFetch) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, clientId]);

  useSubscriptions<
    OnCreateTechnicLibrarySubscription,
    OnUpdateTechnicLibrarySubscription,
    OnDeleteTechnicLibrarySubscription
  >(
    customOnCreateTechnicLibrary,
    customOnUpdateTechnicLibrary,
    customOnDeleteTechnicLibrary,
    (data) => {
      contextDispatch({
        type: 'ADD_TECHNIC_LIBRARY',
        payload: { technicLibrary: data.onCreateTechnicLibrary as TechnicLibrary },
      });
    },
    (data) => {
      contextDispatch({
        type: 'UPDATE_TECHNIC_LIBRARY',
        payload: { technicLibrary: data.onUpdateTechnicLibrary as TechnicLibrary },
      });
    },
    (data) => {
      const { id } = data.onDeleteTechnicLibrary as TechnicLibrary;
      contextDispatch({
        type: 'DELETE_TECHNIC_LIBRARY',
        payload: { id },
      });
    },
    undefined,
    undefined,
    undefined,
    true
  );

  const createTechnicLibrary = async (input: Omit<CreateTechnicLibraryInput, 'id' | 'clientId' | 'readId'>) => {
    const technicLibrary = await mutation<TechnicLibrary, CreateMutationVariables>(customCreateTechnicLibrary, {
      input: {
        ...(cleanInputCreate(input) as CreateInput),
        clientId: getTableClientId(clientId!, TECHNIC_LIBRARY_TABLE_NAME),
        readId: getReadId(clientId!, TECHNIC_LIBRARY_TABLE_NAME),
      },
    });
    contextDispatch({ type: 'ADD_TECHNIC_LIBRARY', payload: { technicLibrary } });
    return technicLibrary;
  };

  const updateTechnicLibrary = async (original: TechnicLibrary, updates: Partial<TechnicLibrary>) => {
    const result = await mutationWithFetchBefore<TechnicLibrary>(
      original.id,
      customGetTechnicLibrary,
      customUpdateTechnicLibrary,
      { input: { ...cleanInputUpdate({ id: original.id, ...updates }, false, true) } }
    );
    contextDispatch({ type: 'UPDATE_TECHNIC_LIBRARY', payload: { technicLibrary: result } });
    return result;
  };

  const technicBulkRename = async (
    technicLibraryId: string,
    newFormat: string,
    includeManuallyNamed: boolean,
    options?: TechnicBulkRenameOptions
  ): Promise<MutationStatus | undefined> => {
    const [outcome] = await Promise.allSettled([
      mutation<MutationStatus, TechnicBulkRenameMutationVariables>(technicBulkRenameMutation, {
        input: { technicLibraryId, newFormat, includeManuallyNamed, ...options },
      }),
    ]);
    return outcome.status === 'fulfilled' ? outcome.value : { status: false, id: technicLibraryId };
  };

  const deleteTechnicLibrary = async (id: string) => {
    const technicLibrary = getTechnicLibrary(id);
    if (!technicLibrary) {
      return;
    }
    await deleteEntityWithFetchBefore<Pick<TechnicLibrary, 'id'>, DeleteMutationVariables>(
      { id },
      customGetTechnicLibrary,
      deleteMutation
    );
    contextDispatch({ type: 'DELETE_TECHNIC_LIBRARY', payload: { id } });
    return technicLibrary;
  };

  const getTechnicLibrary = (id: string): TechnicLibraryWithRules | undefined =>
    technicLibrariesLoading ? undefined : technicLibraries.find((u) => u.id === id);

  const deepDeleteTechnicLibrary = async (id: string): Promise<MutationStatus | undefined> => {
    const result = await mutation<MutationStatus, DeleteEntityByIdMutationVariables>(deleteEntityByIdMutation, {
      input: { entityId: id, entity: DeepDeletableEntityType.TECHNIC_LIBRARY },
    });
    if (result?.status) {
      contextDispatch({ type: 'DELETE_TECHNIC_LIBRARY', payload: { id } });
    }
    return result;
  };

  const values = useMemo(
    () => ({
      technicLibraries,
      getTechnicLibrary,
      createTechnicLibrary,
      updateTechnicLibrary,
      technicBulkRename,
      deleteTechnicLibrary,
      deepDeleteTechnicLibrary,
      technicLibrariesError: undefined,
      technicLibrariesLoading,
      setFetchTechnicLibrary,
      renameProposalQueue,
      suggestRenameForFormatChange,
      dequeueTechnicRenameProposal,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [technicLibraries, technicLibrariesLoading, renameProposalQueue]
  );

  return <TechnicsLibraryContext.Provider value={values}>{children}</TechnicsLibraryContext.Provider>;
};

export const useTechnicsLibrary = (): TechnicsLibraryContext => {
  const context = useContext<TechnicsLibraryContext | null>(TechnicsLibraryContext);

  if (isNil(context)) {
    throw new Error('`useTechnicsLibrary` hook must be used within a `TechnicsLibraryContextProvider` component');
  }
  return context as TechnicsLibraryContext;
};
